/**
 * Pure Auswahl-Helfer für die Checkbox-Liste der Seite "Kunden-Zuordnung"
 * (TenantAssignmentsPage). Die Auswahl bleibt beim Blättern erhalten und wird
 * von der Seite selbst bei Filter-/Suchwechsel geleert (kein Helfer hier nötig,
 * da das schlicht `new Set()` ist).
 */

/**
 * Schaltet die Auswahl eines einzelnen Kunden um, ohne die übrige Auswahl zu
 * verändern.
 * @param selected Bisherige Auswahl (Kunden-IDs).
 * @param id Kunden-ID, deren Haken gesetzt/entfernt wird.
 * @param checked Neuer Haken-Zustand.
 * @returns Neue Auswahl-Menge (bisherige Menge bleibt unverändert).
 */
export function toggleSelection(selected: Set<string>, id: string, checked: boolean): Set<string> {
  const next = new Set(selected);
  if (checked) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

/**
 * Setzt/löscht die Auswahl für alle IDs der aktuellen Seite in einem Schritt
 * ("Alle auf Seite auswählen") — Auswahl auf anderen Seiten bleibt erhalten.
 * @param selected Bisherige Auswahl.
 * @param pageIds Kunden-IDs der aktuell angezeigten Seite.
 * @param checked true = alle auf der Seite markieren, false = alle abwählen.
 * @returns Neue Auswahl-Menge.
 */
export function togglePageSelection(selected: Set<string>, pageIds: string[], checked: boolean): Set<string> {
  const next = new Set(selected);
  for (const id of pageIds) {
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
  }
  return next;
}

/**
 * Zustand der "Alle auf Seite auswählen"-Checkbox, wenn alle IDs der Seite
 * ausgewählt sind (Checkbox erscheint als "checked").
 */
export function isAllOnPageSelected(selected: Set<string>, pageIds: string[]): boolean {
  return pageIds.length > 0 && pageIds.every((id) => selected.has(id));
}

/**
 * Zustand der "Alle auf Seite auswählen"-Checkbox, wenn nur ein Teil der IDs
 * der Seite ausgewählt ist (Checkbox erscheint als "indeterminate").
 */
export function isSomeOnPageSelected(selected: Set<string>, pageIds: string[]): boolean {
  return pageIds.some((id) => selected.has(id));
}
